import {
  Component,
  OnInit,
  ViewEncapsulation,
  ContentChildren,
  QueryList,
  Input,
  AfterContentInit,
  Optional,
  Host,
  Renderer2,
  ElementRef,
} from '@angular/core';
import { PgErrorDirective } from '../../pg-subscript-messages/pg-error.directive';
import { FormGroup, FormControl, FormGroupDirective } from '@angular/forms';

import { PgFieldGridComponent } from '../pg-field-grid.component';

import {
  animateSubscriptHostHeight,
  animateShowSubscriptMessage,
  animateHideSubscriptMessage,
  animateTopSubscriptMessage,
} from '../../pg-subscript-messages/pg-subscript.animations';
import { PromiseQueue } from '../../../core/async/promise-queue';
import { ErrorStateMatcher } from '../../pg-input/input-error-options';
import { AnimationService } from '../../../core/ui/animation.service';

export interface PgFieldGridErrorDisplayState {
  visible?: boolean;
  visibleIndex?: 0;
  visibleHeight?: 0;
  elementHeight?: 0;
  error?: PgErrorDirective;
}
@Component({
  selector: 'pg-field-grid-subscript, grit-field-grid-subscript',
  templateUrl: './pg-field-grid-subscript.component.html',
  styleUrls: ['./pg-field-grid-subscript.component.scss'],
  providers: [PromiseQueue],
})
export class PgFieldGridSubscriptComponent implements OnInit, AfterContentInit {
  @ContentChildren(PgErrorDirective) _errorChildren: QueryList<
    PgErrorDirective
  >;

  @Input() errorStateMatcher: ErrorStateMatcher;

  _errorChildrenControlMap: { [key: string]: PgErrorDirective[] };
  _previousErrorChildrenState: {
    [id: number]: PgFieldGridErrorDisplayState;
  };

  public get hostElement(): HTMLElement {
    return this.elementRef.nativeElement;
  }

  constructor(
    public animationService: AnimationService,
    public renderer: Renderer2,
    public elementRef: ElementRef,
    _defaultErrorStateMatcher: ErrorStateMatcher,
    @Host() private _formGroupDirective: FormGroupDirective,
    @Host() private _fieldGrid: PgFieldGridComponent,
    private _pq: PromiseQueue
  ) {
    if (!this.errorStateMatcher) {
      this.errorStateMatcher = _defaultErrorStateMatcher;
    }
  }

  ngOnInit() {}

  ngAfterContentInit() {
    if (!this._formGroupDirective) {
      throw new Error(
        'PgFieldGridSubscriptComponent should be wrapped under a formGroup'
      );
    }

    this._errorChildrenControlMap = this._errorChildren.reduce(
      (accumulator, currentValue) => {
        if (accumulator[currentValue.controlName]) {
          accumulator[currentValue.controlName].push(currentValue);
        } else {
          accumulator[currentValue.controlName] = [currentValue];
        }
        return accumulator;
      },
      {}
    );

    // Create the initial state for the errrors
    this._previousErrorChildrenState = this._errorChildren.reduce(
      (accumulator, currentValue) => {
        accumulator[currentValue.id] = {
          visible: false,
          error: currentValue,
          visibleIndex: 0,
          visibleHeight: 0,
          // We use this loading opportunity to cache the offsetHeight
          // so we aren't calculating it each CD cycle.
          elementHeight: currentValue.elementRef.nativeElement.offsetHeight,
        };
        return accumulator;
      },
      {}
    );

    this._formGroupDirective.statusChanges.subscribe(changes => {
      this._checkSubscriptState();
    });
    this._fieldGrid.stateChanges.subscribe(() => {
      this._checkSubscriptState();
    });

    this._checkSubscriptState();
  }

  private _checkSubscriptState() {
    const previousState = this._previousErrorChildrenState;
    const subscriptState = this._calculateSubscriptState(previousState);
    this._runSubscriptAnimations(previousState, subscriptState);
    this._previousErrorChildrenState = subscriptState;
  }

  private _calculateSubscriptState(
    previousState: PgFieldGridErrorDisplayState
  ) {
    const state: any = {};
    let visibleIndexAccumulator = 0;
    let visibleHeightAccumulator = 0;
    Object.keys(this._errorChildrenControlMap).forEach(key => {
      const controlErrorArray = this._errorChildrenControlMap[key];

      for (let i = 0; i < controlErrorArray.length; i++) {
        const controlError = controlErrorArray[i];
        const ngControl = this._formGroupDirective.form.get(
          controlError.controlName
        ) as FormControl;
        let errorShown = false;
        const elementHeight = previousState[controlError.id].elementHeight;

        if (
          this.errorStateMatcher.isError(ngControl, this._formGroupDirective) &&
          ngControl.hasError(controlError.for) &&
          !errorShown
        ) {
          state[controlError.id] = {
            elementHeight,
            visible: true,
            visibleHeight: visibleHeightAccumulator,
            error: controlError,
            visibleIndex: visibleIndexAccumulator++,
          };
          visibleHeightAccumulator += elementHeight;
          errorShown = true;
        } else {
          state[controlError.id] = {
            elementHeight,
            visible: false,
            error: controlError,
            visibleIndex: 0,
            visibleHeight: 0,
          };
        }
      }
    });

    return state;
  }

  private _runSubscriptAnimations(
    previousState: PgFieldGridErrorDisplayState,
    nextState: PgFieldGridErrorDisplayState
  ) {
    const leavingAnimations: (() => Promise<any>)[] = [];
    const movingAnimations: (() => Promise<any>)[] = [];
    const enteringAnimations: (() => Promise<any>)[] = [];

    const previousWrapperHeight = this._calculateWrapperHeightForState(
      previousState
    );
    const nextWrapperHeight = this._calculateWrapperHeightForState(nextState);

    let hostHeightAnimation = () => Promise.resolve();
    if (previousWrapperHeight !== nextWrapperHeight) {
      hostHeightAnimation = () =>
        animateSubscriptHostHeight(
          this.animationService,
          this.renderer,
          this.hostElement,
          nextWrapperHeight
        );
    }

    // To determine the animations we need to run, we need to diff the two states
    const stateKeys = Object.keys(nextState);
    for (let i = 0; i < stateKeys.length; i++) {
      const key = stateKeys[i];
      const previousStateElement: PgFieldGridErrorDisplayState =
        previousState[key];
      const nextStateElement: PgFieldGridErrorDisplayState = nextState[key];
      // If for some reason a one of the two state elements is
      // undefined, we are going to ignore it because it's not a state
      // we care about for now.
      if (!previousStateElement || !nextStateElement) {
        break;
      }

      /*
        There are three cases we are animating for.

        1.) the previous state was not visible and the next state is visible
        2.) the previous state was visible and the next state is not visible
        3.) the previous state was visible and the next state is visible and the visibleIndex is different.

        This allows us to smoothly transition error messages
      */

      if (!previousStateElement.visible && nextStateElement.visible) {
        this.renderer.setStyle(
          nextStateElement.error.elementRef.nativeElement,
          'top',
          `${nextStateElement.visibleHeight}px`
        );
        enteringAnimations.push(() =>
          animateShowSubscriptMessage(
            this.animationService,
            this.renderer,
            nextStateElement.error.elementRef.nativeElement
          )
        );
      } else if (previousStateElement.visible && !nextStateElement.visible) {
        leavingAnimations.push(() =>
          animateHideSubscriptMessage(
            this.animationService,
            this.renderer,
            nextStateElement.error.elementRef.nativeElement
          )
        );
      } else if (
        previousStateElement.visible &&
        nextStateElement.visible &&
        previousStateElement.visibleHeight !== nextStateElement.visibleHeight
      ) {
        // this lives as a part of leaving animations
        // because as the leaving messages leave,
        // we want to move messages at the same time.
        // This may end up being false depending on how it looks.
        movingAnimations.push(() =>
          animateTopSubscriptMessage(
            this.animationService,
            this.renderer,
            nextStateElement.error.elementRef.nativeElement,
            previousStateElement.visibleHeight,
            nextStateElement.visibleHeight
          )
        );
      }
    }

    // The wrapper is shrinking
    if (previousWrapperHeight > nextWrapperHeight) {
      return this._pq.queuePromise(() =>
        Promise.resolve()
          .then(() => Promise.all(leavingAnimations.map(a => a())))
          .then(() => Promise.all(movingAnimations.map(a => a())))
          .then(() => Promise.all(enteringAnimations.map(a => a())))
          .then(() => hostHeightAnimation())
      );

      // The wrapper is growing or stagnant
    } else {
      return this._pq.queuePromise(() =>
        hostHeightAnimation()
          .then(() => Promise.all(leavingAnimations.map(a => a())))
          .then(() => Promise.all(movingAnimations.map(b => b())))
          .then(() => Promise.all(enteringAnimations.map(c => c())))
      );
    }
  }

  private _calculateWrapperHeightForState(state: PgFieldGridErrorDisplayState) {
    const values = Object.keys(state).map(k => state[k]);
    return values.reduce((accumulator, current) => {
      return accumulator + (current.visible ? current.elementHeight : 0);
    }, 0);
  }
}
